/**
 * The five questions after a visit (the volunteer sign-out form, decided 29 Sep): what time did you get back;
 * any additional stops, and where (plain text); was everything okay; was there an incident (Yes opens the
 * incident report); anything else. One page, on the volunteer's phone or entered by staff at the desk.
 */
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletionSchema, type CompletionInput } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { ChoiceField, TextArea, TextField, toErrors, type Errors } from '@/features/people/fields';
import { Button } from '@/components/ui/button';

export function CompletionForm({ onSubmit, busy, big, submitLabel }: { onSubmit: (c: CompletionInput) => void; busy?: boolean; big?: boolean; submitLabel: string }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const uid = useId();
  const [c, setC] = useState<Partial<CompletionInput>>({ backAt: '', stops: '', anythingElse: '' });
  const [errs, setErrs] = useState<Errors>({});
  const set = (k: keyof CompletionInput, v: string) => setC((x) => ({ ...x, [k]: v }));
  const go = () => {
    const p = CompletionSchema.safeParse(c);
    if (!p.success) {
      setErrs(toErrors(p.error));
      announce(t('vol.form.fixErrors'), 'assertive');
      return;
    }
    setErrs({});
    onSubmit(p.data);
  };
  const yn = [
    { value: 'yes' as const, label: t('vol.yes') },
    { value: 'no' as const, label: t('vol.no') },
  ];
  return (
    <div className="completion-form" data-testid="completion-form">
      <TextField id={`${uid}-back`} label={t('vol.done.q1')} required value={c.backAt ?? ''} onChange={(v) => set('backAt', v)} error={errs.backAt ? 'required' : undefined} placeholder="3:05" inputMode="numeric" help={t('vol.done.q1Help')} />
      <TextField id={`${uid}-stops`} label={t('vol.done.q2')} value={c.stops ?? ''} onChange={(v) => set('stops', v)} placeholder="CVS, Chase Bank" help={t('vol.done.q2Help')} />
      <ChoiceField id={`${uid}-ok`} label={t('vol.done.q3')} required value={(c.allOkay ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('allOkay', v)} options={yn} error={errs.allOkay ? 'required' : undefined} />
      <ChoiceField id={`${uid}-inc`} label={t('vol.done.q4')} required value={(c.incident ?? '') as 'yes' | 'no' | ''} onChange={(v) => set('incident', v)} options={yn} error={errs.incident ? 'required' : undefined} help={t('vol.done.q4Help')} />
      <TextArea id={`${uid}-else`} label={t('vol.done.q5')} value={c.anythingElse ?? ''} onChange={(v) => set('anythingElse', v)} />
      <Button plain type="button" className={big ? 'big-btn' : 'btn'} onClick={go} disabled={busy} data-testid="completion-send">
        {submitLabel}
      </Button>
    </div>
  );
}
